import React from 'react';
import type { CaseResult, OfferStackItem, MechanismStep, ContentBlock, FAQ } from '../types';
import AnimatedSection from '../components/ui/AnimatedSection';
import CTABand from '../components/funnel/CTABand';
import Seo from '../components/shared/Seo';
import FunnelHero from '../components/funnel/FunnelHero';
import AuthorityBurst from '../components/funnel/AuthorityBurst';
import EpiphanyBridgeContent from '../components/funnel/EpiphanyBridgeContent';
import MechanismDiagram from '../components/funnel/MechanismDiagram';
import OfferStack from '../components/funnel/OfferStack';
import FaqSection from '../components/blog/FaqSection';
import { generateFaqSchema } from '../lib/schema';
import FlippableCard from '../components/ui/FlippableCard';
import PageExtras from '../components/shared/PageExtras';
import { Briefcase } from 'react-feather';
import { useData } from '../context/DataContext';
import LocalizedLink from '../components/ui/LocalizedLink';
import { resolvePracticeAreaPath } from '../lib/utils';
import { useTranslation } from 'react-i18next';

const ResultsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { practiceAreas, caseResults: caseResultsData, firmStats, commonOfferStack, commonRiskReversal } = useData();

  const epiphanyBridgeContent = t('resultsPage.epiphanyBridgeContent', { returnObjects: true }) as ContentBlock[];
  const mechanism = t('resultsPage.mechanism', { returnObjects: true }) as { title: string; subtitle: string; steps: MechanismStep[] };
  const offerStack: OfferStackItem[] = commonOfferStack;
  const riskReversal = commonRiskReversal;
  const faqs = t('resultsPage.faqs', { returnObjects: true }) as FAQ[];

  const faqSchema = generateFaqSchema(faqs);
  const pageSchemas = [faqSchema];

  const renderResult = (result: CaseResult, index: number) => (
      <FlippableCard
          key={index}
          frontContent={
              <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-400/10 to-transparent p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                  <p className="text-lg tracking-wide text-white">{result?.caseType || 'Successful Settlement'}</p>
                  <div className="text-5xl font-extrabold gold-gradient-text mt-1">{result?.amount || 'Confidential'}</div>
              </div>
          }
          backContent={
              <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-tr from-yellow-400/20 to-brand-dark/90 p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                  {result?.practiceAreaSlug && (
                      <LocalizedLink to={resolvePracticeAreaPath(result.practiceAreaSlug, practiceAreas.map((p) => p.slug))} className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors rounded-full px-4 py-2 bg-white/10 border border-white/10 hover:bg-white/20 glass-effect">
                          <Briefcase size={16} /> {t('aboutPage.proofSeeMethodButton')}
                      </LocalizedLink>
                  )}
              </div>
          }
      />
  );

  return (
    <>
      <Seo
          title={t('resultsPage.seoTitle')}
          description={t('resultsPage.seoDescription')}
          schema={pageSchemas}
      />
      <div>
        <FunnelHero
          headline={t('resultsPage.heroHeadline')}
          subheadline={t('resultsPage.heroSubheadline')}
          imageUrl="https://images.pexels.com/photos/5668484/pexels-photo-5668484.jpeg?auto=compress&cs=tinysrgb&w=1600"
        />

        <AuthorityBurst stats={firmStats} />

        <div className="py-24 bg-brand-dark">
          <AnimatedSection>
              <EpiphanyBridgeContent content={epiphanyBridgeContent} />
          </AnimatedSection>
        </div>

        <MechanismDiagram mechanism={mechanism} />
        
        <AnimatedSection className="py-24">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('resultsPage.resultsTitle')}</h2>
              <p className="mt-4 text-lg text-white/80">{t('resultsPage.resultsSubtitle')}</p>
            </div>
            {caseResultsData.length > 0 && (
              <div className="space-y-8">
                {caseResultsData.map(renderResult)}
              </div>
            )}
             <p className="mt-12 text-center text-sm text-white/60 italic">
                {t('resultsPage.resultsDisclaimer')}
              </p>
          </div>
        </AnimatedSection>

        <OfferStack offerStack={offerStack} />

        <div className="py-24 bg-white/5">
            <FaqSection 
              faqs={faqs}
              title={t('resultsPage.faqTitle')}
            />
         </div>

         <AnimatedSection className="py-24">
           <div className="mx-auto max-w-4xl px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{riskReversal.title}</h2>
              <p className="mt-6 text-lg text-white/80">{riskReversal.description}</p>
           </div>
         </AnimatedSection>

        <PageExtras pageKey="results" />

        <CTABand
          headline={t('resultsPage.ctaBandHeadline')}
          subheadline={t('resultsPage.ctaBandSubheadline')}
          buttonText={t('resultsPage.ctaBandButton')}
          buttonLink="/contact"
          isPrimary={true}
        />

      </div>
    </>
  );
};

export default ResultsPage;